import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import styles from "../shared/styles/PolicyModals.module.scss";
import builderStyles from "../shared/PolicyBuilder/PolicyBuilder.module.scss";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { BaseModal, ModalHeader, modalService } from "@whatslly/ui-components/modals";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { TextArea } from "@whatslly/ui-components/v2/BaseElements/TextArea";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IDLPStore } from "./IDLPStore";
import { InjectDLPStore } from "./IDLPStore";
import { observer } from "mobx-react-lite";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { getPIICategories } from "./DLPPIICategories";
import type { DLPRuleCondition } from "@whatslly/types/dlp/DLPRule";
import { DLPRule } from "@whatslly/types/dlp/DLPRule";
import { Checkbox } from "@whatslly/ui-components/v2/Checkbox";
import { TestRuleModal } from "./TestRuleModal";
import { RiTestTubeFill } from "react-icons/ri";
import { ActionsMenu, ActionType } from "@whatslly/ui-components/ActionsMenu";
import { parseCSV } from "@whatslly/commons/csvUtils";
import { ImportExpressionsModal } from "./ImportExpressionsModal/ImportExpressionsModal";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { Button } from "@whatslly/ui-components/v2/Button";
import { SectionHeader } from "../shared/PolicyBuilder/sections/SectionHeader";
import { Tooltip } from "@whatslly/ui-components/Tooltip";

interface DLPRuleModalProps extends IModalInstance<DLPRule> {
  rule?: DLPRule;
  allowDlpAI?: boolean;
}

const DEFAULT_CONDITIONS = {
  has_expression: { type: "expression", caseSensitive: false, expression: "", max: 1, oneTime: false } as DLPRuleCondition,
  PII: { type: "PII", category: "", max: 1, oneTime: false } as DLPRuleCondition,
  metadata: { type: "metadata", key: "", value: "", oneTime: false } as DLPRuleCondition,
  image_unappropriate: { type: "image_unappropriate", oneTime: true } as DLPRuleCondition,
} as const;

const CONDITION_CLASSES = {
  PII: styles.purple,
  expression: styles.orange,
  metadata: styles.blue,
  image_unappropriate: styles.red,
};

const CONDITION_OPTIONS = Object.entries(DEFAULT_CONDITIONS).map(([key, condition]) => ({
  key: key as keyof typeof DEFAULT_CONDITIONS,
  condition,
  titleKey: `dlp_page.conditions.${key}`,
  descriptionKey: `dlp_page.rule_modal.condition_types.${key}`,
})) as Array<{
  key: keyof typeof DEFAULT_CONDITIONS;
  condition: DLPRuleCondition;
  titleKey: string;
  descriptionKey: string;
}>;

const CONDITION_OPTION_BY_TYPE: Record<DLPRuleCondition["type"], (typeof CONDITION_OPTIONS)[number]> = {
  expression: CONDITION_OPTIONS[0],
  PII: CONDITION_OPTIONS[1],
  metadata: CONDITION_OPTIONS[2],
  image_unappropriate: CONDITION_OPTIONS[3],
};

const ConditionsDescriptionInput: React.FC<{
  updateCondition: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
}> = ({ updateCondition, value }) => {
  const { t } = useTranslation();

  return (
    <TextInput
      data-testid="dlp_rule_condition_description"
      removeClear
      placeholder={t("dlp_page.conditions.description")}
      value={value}
      paddingInline="none"
      className={styles.descriptionInput}
      onChange={updateCondition}
    />
  );
};

export const DLPRuleModal: React.FC<DLPRuleModalProps> = observer(({ rule, allowDlpAI: allowDlpAIProp, closeModal }) => {
  const store = useDependencyInjector<IDLPStore>(InjectDLPStore);
  const { t } = useTranslation();

  const [name, setName] = useState(rule?.name ?? "");
  const [description, setDescription] = useState(rule?.description ?? "");
  const [conditions, setConditions] = useState<DLPRuleCondition[]>(() => rule?.conditions ?? []);

  const validatedConditions = useMemo(
    () =>
      conditions.map((condition) => {
        if (condition.type !== "expression") {
          return { ...condition, valid: true };
        }

        let valid = false;

        try {
          new RegExp(condition.expression, `g${condition.caseSensitive ? "" : "i"}`);
          valid = true;
        } catch {
          // ignore
        }

        return {
          ...condition,
          valid,
        };
      }),
    [conditions]
  );

  const resolvedAllowDlpAI = allowDlpAIProp !== undefined ? allowDlpAIProp : store.allowDlpAI.state === "fulfilled" && store.allowDlpAI.value;

  const canAddCondition = useCallback(
    ({ condition }: (typeof CONDITION_OPTIONS)[number]) => {
      const isOneTimeAndExists = condition.oneTime && conditions.some((currentCondition) => currentCondition.type === condition.type);
      if (isOneTimeAndExists) return false;
      if (condition.type === "PII" || condition.type === "image_unappropriate") return resolvedAllowDlpAI;
      return true;
    },
    [conditions, resolvedAllowDlpAI]
  );

  const addCondition = (condition: DLPRuleCondition) => setConditions((current) => [...current, { ...condition }]);
  const removeCondition = (index: number) => setConditions((current) => current.filter((_, currentIndex) => currentIndex !== index));
  const updateCondition = (condition: DLPRuleCondition, index: number) =>
    setConditions((current) => current.map((currentCondition, currentIndex) => (currentIndex === index ? condition : currentCondition)));

  const importExpressionsCSV = useCallback(async () => {
    const result = await modalService.openModal(ImportExpressionsModal).promise;
    if (!result?.csvContent) return;

    const { data, header } = parseCSV(result.csvContent);
    const expressionHeader = header.find((headerName) => headerName.toLowerCase() === "expression");

    if (!expressionHeader) {
      modalService.openModal(Toast, {
        text: t("user_management.import_users_csv_invalid_format", { header: header.join(", ") }),
        type: "error",
      });
      return;
    }

    const newConditions = data.map((row) => ({
      type: "expression" as const,
      expression: row["expression"] ?? row[header[0]] ?? "",
      caseSensitive: row["caseSensitive"] === "true" || row["case_sensitive"] === "true" || row[header[1]] === "true" || false,
      max: Number(row["max"]) || Number(row["maximum"]) || Number(row[header[2]]) || 1,
      countUniqueValuesOnly:
        row["countUnique"] === "true" || row["count_unique"] === "true" || row["Count Unique"] === "true" || row[header[3]] === "true" || false,
    }));

    setConditions((current) => [...current, ...newConditions]);
  }, [t]);

  const isSubmitDisabled = useMemo(
    () =>
      !name ||
      !conditions.length ||
      validatedConditions.some((condition) => !condition.valid) ||
      conditions.some(
        (condition) =>
          (condition.type === "expression" && (!condition.expression || condition.max < 1)) ||
          (condition.type === "PII" && (!condition.category || condition.max < 1)) ||
          (condition.type === "metadata" && (!condition.key || !condition.value))
      ),
    [conditions, name, validatedConditions]
  );

  return (
    <BaseModal
      headerClassName={styles.title}
      header={<ModalHeader closeModal={closeModal}>{rule ? t("dlp_page.update_rule") : t("dlp_page.create_rule")}</ModalHeader>}
      closeModal={closeModal}
      className={styles.dlpModal}
      closeOnClickOutside={false}
      closeOnEscape={false}
      bodyClassName={styles.dlpModalBody}
    >
      <div className={styles.main}>
        <section className={styles.modalSection}>
          <SectionHeader
            title={t("dlp_page.rule_modal.steps.details.title")}
            description={t("dlp_page.rule_modal.steps.details.description")}
            className={styles.sectionHeader}
          />
          <div className={classNames(builderStyles.stepContent, styles.stepContent)}>
            <div className={builderStyles.field}>
              <div className={builderStyles.inline}>
                <label htmlFor="dlp-rule-name">{t("dlp_page.rule_name")}</label>
                <span className={builderStyles.requiredIndicator}>{t("dlp_page.required")}</span>
              </div>
              <TextInput
                autoFocus
                id="dlp-rule-name"
                placeholder={t("dlp_page.rule_name")}
                value={name}
                paddingInline="none"
                data-testid="dlp_rule_name"
                onClear={() => setName("")}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className={builderStyles.field}>
              <label htmlFor="dlp-rule-description">{t("dlp_page.description")}</label>
              <TextArea
                id="dlp-rule-description"
                placeholder={t("dlp_page.description")}
                value={description}
                paddingInline="none"
                data-testid="dlp_rule_description"
                className={classNames(builderStyles.textArea, styles.modalTextArea)}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          </div>
        </section>
        <section>
          <div className={styles.stickyHeader}>
            <SectionHeader
              title={t("dlp_page.rule_modal.steps.conditions.title")}
              description={t("dlp_page.rule_modal.steps.conditions.description")}
              className={styles.sectionHeader}
            >
              <div className={styles.sectionToolbar}>
                <Button
                  className={styles.importBtn}
                  onClick={importExpressionsCSV}
                  paddingInline="small"
                  svg={{ iconName: "upload", width: 20, height: 20 }}
                >
                  {t("dlp_page.conditions.import_expression_csv")}
                </Button>
              </div>
            </SectionHeader>
            <div className={classNames(styles.conditionsRow)}>
              {CONDITION_OPTIONS.map((item) => {
                const isDisabled = !canAddCondition(item);
                return (
                  <Tooltip value={t(item.descriptionKey)} key={item.key}>
                    <button
                      type="button"
                      className={classNames(styles.condition, {
                        [styles.disabled]: isDisabled,
                      })}
                      data-testid={`dlp_rule_add_condition_${item.key}`}
                      onClick={() => addCondition(item.condition)}
                      disabled={isDisabled}
                    >
                      <span className={classNames(styles.conditionTypeMarker, CONDITION_CLASSES[item.condition.type])} />
                      <span>{t(item.titleKey)}</span>
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
          <div className={styles.stepContent}>
            {conditions.length === 0 ? (
              <div className={styles.emptyState}>{t("dlp_page.rule_modal.condition_types.empty")}</div>
            ) : (
              <div className={styles.conditionCardList}>
                {conditions.map((condition, index) => {
                  return (
                    <React.Fragment key={`${condition.type}-${index}`}>
                      <RuleCondition
                        condition={condition}
                        index={index}
                        isValid={validatedConditions[index]?.valid ?? false}
                        updateCondition={(nextCondition) => updateCondition(nextCondition, index)}
                        removeCondition={() => removeCondition(index)}
                      />
                      {conditions.length - 1 !== index && (
                        <div className={styles.conditionEditorOr}>{t("dlp_page.conditions.or_between_expression")}</div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
      <ModalButtons
        className={styles.buttons}
        isSubmitDisabled={isSubmitDisabled}
        testId="dlp_rule_submit"
        submitText={rule ? t("dlp_page.update_rule") : t("dlp_page.create_rule")}
        onSubmit={() =>
          closeModal({
            id: rule?.id ?? crypto.randomUUID(),
            name,
            description,
            conditions,
          })
        }
        onCancel={() => closeModal()}
      />
    </BaseModal>
  );
});

const RuleCondition: React.FC<{
  condition: DLPRuleCondition;
  index: number;
  isValid: boolean;
  updateCondition: (condition: DLPRuleCondition) => void;
  removeCondition: () => void;
}> = observer(({ condition, index, isValid, updateCondition, removeCondition }) => {
  const { t } = useTranslation();
  const conditionOption = CONDITION_OPTION_BY_TYPE[condition.type];

  return (
    <section className={styles.conditionCard}>
      <div className={styles.conditionCardHeader}>
        <div className={styles.conditionCardHeading}>
          <span className={classNames(styles.conditionTypeMarker, CONDITION_CLASSES[condition.type])} />
          <div className={styles.conditionCardTitle}>
            <h3>{t(conditionOption.titleKey)}</h3>
            <div>
              <ConditionsDescriptionInput
                value={condition.description || ""}
                updateCondition={(event) => updateCondition({ ...condition, description: event.target.value })}
              />
            </div>
          </div>
        </div>
        <ActionsMenu
          className={styles.conditionActionsMenu}
          testId="dlp-rule-actions-menu"
          actions={
            [
              {
                children: t("dlp_page.conditions.delete_expression"),
                icon: "trash",
                onClick: () => removeCondition(),
                testId: "remove-dlp-condition",
              },
              condition.type === "expression" || condition.type === "PII" || condition.type === "metadata"
                ? {
                    children: t("dlp_page.conditions.test_condition"),
                    icon: <RiTestTubeFill />,
                    testId: "open-test-rule-modal-button",
                    onClick: async () => {
                      const result = await modalService.openModal(TestRuleModal, {
                        condition,
                      }).promise;
                      if (!result) {
                        return;
                      }
                      updateCondition(result.condition);
                    },
                  }
                : undefined,
            ].filter(Boolean) as ActionType[]
          }
        />
      </div>
      <DLPRuleEditor condition={condition} index={index} isValid={isValid} updateCondition={updateCondition} />
    </section>
  );
});

const DLPRuleEditor: React.FC<{
  condition: DLPRuleCondition;
  index: number;
  isValid: boolean;
  updateCondition: (condition: DLPRuleCondition) => void;
}> = observer(({ condition, index, isValid, updateCondition }) => {
  const { t } = useTranslation();
  const piiCategories = useMemo(() => getPIICategories(t), [t]);

  const maxOccurrencesField =
    condition.type !== "metadata" && condition.type !== "image_unappropriate" ? (
      <div className={classNames(builderStyles.field, styles.compactField, styles.floatingLabelField)}>
        <label>{t("dlp_page.conditions.max_occurrences")}</label>
        <TextInput
          type="number"
          min={1}
          className={classNames(styles.conditionMax, styles.textInput)}
          data-testid="dlp_rule_condition_max"
          removeClear
          placeholder={t("dlp_page.conditions.max_occurrences")}
          value={condition.max}
          paddingInline="none"
          onChange={(event) => updateCondition({ ...condition, max: Number(event.target.value) })}
        />
      </div>
    ) : null;

  const expressionFlags = condition.type === "expression" ? `/g${condition.caseSensitive ? "" : "i"}` : "";

  switch (condition.type) {
    case "PII":
      return (
        <div className={styles.conditionCardBody}>
          <div className={styles.conditionForm}>
            <div className={styles.fieldsGroup}>
              <div className={classNames(builderStyles.field, styles.fill, styles.floatingLabelField)}>
                <label>{t("dlp_page.conditions.pii_category")}</label>
                <Select
                  paddingInline="none"
                  selectSize="xs"
                  controlClassName={builderStyles.selectControl}
                  isSearchable
                  placeholder={t("dlp_page.search_placeholder")}
                  data-testid="dlp_rule_condition_pii_categories"
                  value={condition.category ? { value: condition.category, label: condition.category } : null}
                  onChange={(value: (typeof piiCategories)[number] | null) => updateCondition({ ...condition, category: value?.value ?? "" })}
                  options={piiCategories}
                />
              </div>
              <div className={styles.conditionSettingsRow}>{maxOccurrencesField}</div>
            </div>
            <div className={styles.checkboxField}>
              <Checkbox
                id={`dlp_rule_condition_count_unique_pii_${index}`}
                testId="dlp_rule_condition_count_unique_pii"
                value={Boolean(condition.countUniqueValuesOnly)}
                size="large"
                onChange={(event) => updateCondition({ ...condition, countUniqueValuesOnly: event.target.checked })}
              />
              <label htmlFor={`dlp_rule_condition_count_unique_pii_${index}`}>{t("dlp_page.conditions.count_unique_values_only")}</label>
            </div>
          </div>
        </div>
      );
    case "expression":
      return (
        <div className={styles.conditionCardBody}>
          <div className={styles.conditionForm}>
            <div className={styles.fieldsGroup}>
              <div className={classNames(builderStyles.field, styles.fill, styles.floatingLabelField)}>
                <label>{t("dlp_page.conditions.expression")}</label>
                <div className={styles.expression}>
                  <span className={styles.expressionPrefix}>/</span>
                  <TextInput
                    data-testid="dlp_rule_condition_expression"
                    removeClear
                    className={classNames(styles.expressionInput, styles.textInput)}
                    placeholder={t("dlp_page.conditions.expression")}
                    value={condition.expression}
                    paddingInline="none"
                    onChange={(event) => updateCondition({ ...condition, expression: event.target.value })}
                  />
                  <span className={styles.expressionSuffix}>{expressionFlags}</span>
                </div>
              </div>
              {maxOccurrencesField}
            </div>
            <div className={styles.fieldsGroup}>
              <div className={styles.checkboxField}>
                <Checkbox
                  id={`dlp_rule_case_sensitive_${index}`}
                  testId="dlp_rule_case_sensitive"
                  value={condition.caseSensitive}
                  size="large"
                  onChange={(event) => updateCondition({ ...condition, caseSensitive: event.target.checked })}
                />
                <label htmlFor={`dlp_rule_case_sensitive_${index}`}>{t("dlp_page.conditions.expression_casesensitive")}</label>
              </div>
              <div className={styles.checkboxField}>
                <Checkbox
                  id={`dlp_rule_condition_count_unique_expression_${index}`}
                  testId="dlp_rule_condition_count_unique_expression"
                  value={Boolean(condition.countUniqueValuesOnly)}
                  size="large"
                  onChange={(event) => updateCondition({ ...condition, countUniqueValuesOnly: event.target.checked })}
                />
                <label htmlFor={`dlp_rule_condition_count_unique_expression_${index}`}>{t("dlp_page.conditions.count_unique_values_only")}</label>
              </div>
            </div>
          </div>
          {!isValid && (
            <div className={styles.invalidExpression} data-testid="invalid-expression">
              {t("dlp_page.conditions.invalid_expression")}
            </div>
          )}
        </div>
      );
    case "metadata":
      return (
        <div className={styles.conditionCardBody}>
          <div className={styles.conditionForm}>
            <div className={styles.fieldsGroup}>
              <div className={classNames(builderStyles.field, styles.fill, styles.floatingLabelField)}>
                <label>{t("dlp_page.conditions.metadata_key")}</label>
                <TextInput
                  data-testid="dlp_rule_condition_metadata_key"
                  removeClear
                  className={classNames(builderStyles.textInput)}
                  value={condition.key}
                  paddingInline="none"
                  onChange={(event) => updateCondition({ ...condition, key: event.target.value })}
                />
              </div>
              <div className={classNames(builderStyles.field, styles.fill, styles.floatingLabelField)}>
                <label>{t("dlp_page.conditions.metadata_value")}</label>
                <TextInput
                  data-testid="dlp_rule_condition_metadata_value"
                  removeClear
                  className={classNames(builderStyles.textInput)}
                  value={condition.value}
                  paddingInline="none"
                  onChange={(event) => updateCondition({ ...condition, value: event.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
      );
  }
});
